import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdOutlineAttachMoney,
  MdOutlineCreditCard,
  MdOutlinePayments,
  MdOutlinePix,
  MdOutlinePointOfSale,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { PLAN_REQUESTS, POINT } from "../../config/apiConfig";
import Modal from "../modals/Modal";
import { formatMoney } from "../crm/crmHelpers";
import PointChargePanel from "./PointChargePanel";
import {
  describeInstallment,
  isCardMethod,
  PAYMENT_METHODS,
  parseMoneyInput,
  POINT_UNAVAILABLE_TEXT,
  toMoneyInput,
} from "./eventHelpers";

/*
  Cobrar o plano de um cadastro do evento.

  As quatro formas são as da comanda do app (crédito, débito, Pix e dinheiro).

  - Pix e dinheiro: a equipe recebeu e confirma aqui. Confirmar aprova o pedido
    de plano: lança o pagamento, troca o plano e estende o vencimento no mesmo
    passo.
  - Crédito e débito: o valor vai DIRETO para a maquininha da conta Mercado Pago
    da Relinq. A cliente passa o cartão (no crédito, escolhe as parcelas na
    máquina) e, quando o Mercado Pago confirma, o servidor libera o plano sozinho:
    esta tela só acompanha (`PointChargePanel`). Se a maquininha não estiver
    disponível, ou a equipe passou o cartão em outra, dá para registrar à mão
    (mesmo caminho do Pix e do dinheiro).

  A forma NÃO vem marcada: é dinheiro entrando, e quem confirma escolhe de
  propósito em vez de herdar um padrão. O OPERADOR também não: se o evento da
  cliente tem operadores cadastrados, quem cobra precisa dizer quem é (o servidor
  também exige), e isso vai para o histórico dela. Evento sem operadores, ou
  cliente sem evento, não pergunta.

  Na maquininha o valor pode ser MENOR que o do plano (desconto do evento), nunca
  maior: o servidor também recusa, mas a tela avisa antes.

  Erros aparecem DENTRO do modal. O aviso da página fica atrás do fundo borrado,
  e quem confirmava via "nada acontecer".
*/

const campoClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100";

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";

const METHOD_ICON = {
  CREDIT: MdOutlineCreditCard,
  DEBIT: MdOutlinePayments,
  PIX: MdOutlinePix,
  CASH: MdOutlineAttachMoney,
};

const ChargePlanModal = ({ signup, onClose, onCharged }) => {
  const queryClient = useQueryClient();
  const { company, request, answers } = signup;
  const amount = Number(request.amount);

  const operators = useMemo(() => signup.event?.operators || [], [signup.event]);
  const [operatorId, setOperatorId] = useState(null);
  const [method, setMethod] = useState(null);
  const [installments, setInstallments] = useState(1);
  const [valueText, setValueText] = useState(toMoneyInput(amount));
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  // A cobrança que está na maquininha (ou o último resultado dela). Começa pela
  // que o servidor já tinha, para quem fechou a janela e voltou.
  const [pointCharge, setPointCharge] = useState(signup.pointCharge || null);
  // A equipe optou por registrar à mão mesmo com a maquininha disponível.
  const [manual, setManual] = useState(false);
  const [terminalId, setTerminalId] = useState("");
  // Brindes do plano que o estoque do evento não cobre: a equipe confirma que
  // sabe antes de cobrar (o servidor não trava a venda).
  const giftShortage = useMemo(
    () => (signup.gifts?.items || []).filter((item) => item.short),
    [signup.gifts]
  );
  const [giftAck, setGiftAck] = useState(false);

  const value = parseMoneyInput(valueText);
  const changedValue = value !== null && value !== amount;

  const terminals = useQuery(
    ["events", "point-terminals"],
    async () => {
      const { data } = await axios.get(POINT.TERMINALS);
      return data.data;
    },
    { staleTime: 20_000, refetchInterval: false }
  );
  const pointInfo = terminals.data;
  const terminalList = useMemo(() => pointInfo?.terminals || [], [pointInfo]);
  const canUsePoint = Boolean(pointInfo?.configured) && terminalList.length > 0;
  const viaPoint = isCardMethod(method) && canUsePoint && !manual;

  // Uma maquininha só: já vem escolhida. Mais de uma: a primeira, e a equipe troca.
  useEffect(() => {
    if (!terminalId && terminalList.length) setTerminalId(String(terminalList[0].id));
  }, [terminalId, terminalList]);

  const maxInstallments = Math.max(Number(request.maxInstallments) || 1, 1);
  const installmentOptions = useMemo(
    () => Array.from({ length: maxInstallments }, (_, index) => index + 1),
    [maxInstallments]
  );

  const charge = useMutation(
    () =>
      axios.post(`${PLAN_REQUESTS.APPROVE}/${request.id}/approve`, {
        method,
        ...(method === "CREDIT" ? { installments } : {}),
        value,
        ...(operatorId ? { operatorId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    {
      onSuccess: ({ data }) => onCharged?.(data.data),
      onError: (err) =>
        setError(
          err.response?.data?.message ||
            "Não foi possível registrar o pagamento. Confira a conexão e tente de novo."
        ),
    }
  );

  const sendToMachine = useMutation(
    () =>
      axios.post(`${PLAN_REQUESTS.POINT_CHARGE}/${request.id}/point-charge`, {
        terminalId: Number(terminalId),
        type: method,
        amount: value,
        ...(operatorId ? { operatorId } : {}),
      }),
    {
      onSuccess: ({ data }) => {
        setPointCharge(data.data);
        // A lista passa a mostrar a cobrança em andamento neste cadastro.
        queryClient.invalidateQueries(["events", "signups"]);
      },
      onError: (err) =>
        setError(
          err.response?.data?.message ||
            "Não foi possível enviar para a maquininha. Confira a conexão e tente de novo."
        ),
    }
  );

  const busy = charge.isLoading || sendToMachine.isLoading;
  // Painel da maquininha no lugar do formulário: há cobrança em andamento, ou um
  // resultado que a equipe precisa ler (recusa, revisão...). "Cobrar de novo"
  // volta ao formulário.
  const showPanel = Boolean(pointCharge);

  const overLimit = viaPoint && value !== null && value > amount;
  const blockReason = operators.length && !operatorId
    ? "Escolha quem está cobrando"
    : !method
    ? "Escolha como foi pago"
    : value === null || value <= 0
    ? "Informe um valor maior que zero"
    : overLimit
    ? "O valor passa do total do plano"
    : viaPoint && !terminalId
    ? "Escolha a maquininha"
    : giftShortage.length && !giftAck
    ? "Confirme que o brinde está sem estoque"
    : null;

  const confirm = () => {
    setError(null);
    if (viaPoint) sendToMachine.mutate();
    else charge.mutate();
  };

  const unavailableText =
    isCardMethod(method) && !canUsePoint && !terminals.isLoading
      ? POINT_UNAVAILABLE_TEXT[pointInfo?.reason] ||
        (terminals.isError
          ? "Não foi possível consultar a maquininha da Relinq agora."
          : null)
      : null;

  const confirmLabel = busy
    ? viaPoint
      ? "Enviando..."
      : "Registrando..."
    : blockReason ||
      (viaPoint
        ? `Enviar ${formatMoney(value)} para a maquininha`
        : `Confirmar ${formatMoney(value)} e liberar o plano`);

  return (
    <Modal
      isOpen
      onClose={() => !busy && onClose()}
      size="md"
      icon={MdOutlinePointOfSale}
      title="Cobrar plano"
      subtitle={company.name}
      closeOnBackdrop={!busy}
      footer={
        showPanel ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Fechar
            </button>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={Boolean(blockReason) || busy}
              className="rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-pink-700 disabled:opacity-40"
            >
              {confirmLabel}
            </button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}

        <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-inset ring-slate-200">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Plano a liberar
          </p>
          <p className="mt-0.5 text-base font-bold text-slate-900">
            {request.planName} · {request.billingCycleLabel}
          </p>
          <p className="mt-2 text-3xl font-bold leading-none text-slate-900">
            {formatMoney(amount)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {request.planId === "DIAMOND"
              ? "Equipe inteira inclusa"
              : `${request.numberOfUsers} profissional(is) × ${formatMoney(
                  request.monthlyPrice
                )} × ${request.months} mês(es)`}
            {answers?.ownerName ? ` · ${answers.ownerName}` : ""}
          </p>
        </div>

        {showPanel ? (
          <PointChargePanel
            charge={pointCharge}
            onChange={setPointCharge}
            onRetry={() => {
              setPointCharge(null);
              setError(null);
            }}
            onDone={(done) => {
              queryClient.invalidateQueries(["events", "signups"]);
              onCharged?.(done);
            }}
          />
        ) : (
          <>
            {giftShortage.length ? (
              <div className="rounded-2xl bg-amber-50 p-3 ring-1 ring-inset ring-amber-200">
                <p className="text-sm font-bold text-amber-900">
                  Este plano dá brinde e o estoque não cobre
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
                  {giftShortage.map((item) => (
                    <li key={item.giftId}>
                      <strong>
                        {item.quantity} × {item.name}
                      </strong>
                      {": "}
                      {item.remaining > 0
                        ? `só restam ${item.remaining}`
                        : item.remaining === 0
                        ? "o estoque acabou"
                        : `já faltam ${Math.abs(item.remaining)} para quem pagou antes`}
                    </li>
                  ))}
                </ul>
                <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs font-semibold text-amber-900">
                  <input
                    type="checkbox"
                    checked={giftAck}
                    onChange={(e) => setGiftAck(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-amber-400 text-pink-600 focus:ring-pink-300"
                  />
                  <span>
                    Cobrar mesmo assim. O brinde que faltar fica devendo para a
                    cliente.
                  </span>
                </label>
              </div>
            ) : null}

            {operators.length ? (
              <fieldset>
                <legend className={labelClass}>Quem está cobrando</legend>
                <div className="flex flex-wrap gap-2" role="radiogroup">
                  {operators.map((operator) => {
                    const active = operatorId === operator.id;

                    return (
                      <button
                        key={operator.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => {
                          setOperatorId(operator.id);
                          setError(null);
                        }}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                          active
                            ? "border-pink-500 bg-pink-600 text-white ring-2 ring-pink-100"
                            : "border-slate-200 bg-white text-slate-700 hover:border-pink-200"
                        }`}
                      >
                        {operator.name}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Fica registrado na cobrança e no histórico da cliente.
                </p>
              </fieldset>
            ) : null}

            <fieldset>
              <legend className={labelClass}>Como foi pago</legend>
              <div className="grid grid-cols-2 gap-2" role="radiogroup">
                {PAYMENT_METHODS.map((option) => {
                  const Icon = METHOD_ICON[option.value];
                  const active = method === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => {
                        setMethod(option.value);
                        setError(null);
                        if (option.value !== "CREDIT") setInstallments(1);
                      }}
                      className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition ${
                        active
                          ? "border-pink-500 bg-pink-50 ring-2 ring-pink-100"
                          : "border-slate-200 bg-white hover:border-pink-200"
                      }`}
                    >
                      <span
                        className={`rounded-lg p-1.5 ${
                          active
                            ? "bg-pink-600 text-white"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        <Icon size={18} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-slate-900">
                          {option.label}
                        </span>
                        <span className="block text-[11px] text-slate-500">
                          {option.hint}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {isCardMethod(method) && terminals.isLoading ? (
              <p className="text-xs text-slate-500">Consultando a maquininha...</p>
            ) : null}

            {viaPoint ? (
              <div className="space-y-2 rounded-2xl border border-pink-200 bg-pink-50/50 p-3">
                <p className="text-xs text-slate-700">
                  O valor vai direto para a maquininha. A cliente passa o cartão
                  {method === "CREDIT"
                    ? " e escolhe as parcelas na própria máquina"
                    : ""}
                  , e o plano é liberado sozinho quando o pagamento for aprovado.
                </p>
                {terminalList.length > 1 ? (
                  <div>
                    <label className={labelClass} htmlFor="event-terminal">
                      Maquininha
                    </label>
                    <select
                      id="event-terminal"
                      value={terminalId}
                      onChange={(event) => setTerminalId(event.target.value)}
                      className={campoClass}
                    >
                      {terminalList.map((terminal) => (
                        <option key={terminal.id} value={terminal.id}>
                          {terminal.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-xs font-semibold text-slate-800">
                    Maquininha: {terminalList[0]?.name}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setManual(true);
                    setError(null);
                  }}
                  className="text-[11px] font-semibold text-slate-500 underline"
                >
                  Passei em outra maquininha, registrar à mão
                </button>
              </div>
            ) : null}

            {isCardMethod(method) && canUsePoint && manual ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-800 ring-1 ring-inset ring-sky-200">
                <span>
                  Registro à mão: passe o cartão na maquininha da equipe e
                  confirme aqui.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setManual(false);
                    setError(null);
                  }}
                  className="font-semibold underline"
                >
                  Usar a maquininha da Relinq
                </button>
              </div>
            ) : null}

            {unavailableText ? (
              <p className="rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-800 ring-1 ring-inset ring-sky-200">
                {unavailableText} Passe o cartão na maquininha da equipe e
                confirme aqui.
              </p>
            ) : null}

            {method === "CREDIT" && !viaPoint ? (
              <div>
                <label className={labelClass} htmlFor="event-installments">
                  Parcelas
                </label>
                <select
                  id="event-installments"
                  value={installments}
                  onChange={(event) => setInstallments(Number(event.target.value))}
                  className={campoClass}
                >
                  {installmentOptions.map((count) => (
                    <option key={count} value={count}>
                      {describeInstallment(value ?? amount, count)}
                    </option>
                  ))}
                </select>
                {maxInstallments === 1 ? (
                  <p className="mt-1 text-[11px] text-slate-500">
                    O plano mensal não parcela.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="event-value">
                  Valor cobrado
                </label>
                <input
                  id="event-value"
                  value={valueText}
                  onChange={(event) => {
                    setValueText(event.target.value);
                    setError(null);
                  }}
                  inputMode="decimal"
                  className={campoClass}
                />
                {overLimit ? (
                  <p className="mt-1 text-[11px] font-semibold text-rose-700">
                    Na maquininha o valor não pode passar de {formatMoney(amount)}.
                  </p>
                ) : changedValue ? (
                  <p className="mt-1 text-[11px] font-semibold text-amber-700">
                    Diferente do valor do plano ({formatMoney(amount)}).
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Só mude se combinar desconto.
                  </p>
                )}
              </div>
              {viaPoint ? null : (
                <div>
                  <label className={labelClass} htmlFor="event-note">
                    Observação
                  </label>
                  <input
                    id="event-note"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={200}
                    placeholder="Ex.: final 4321 do cartão"
                    className={campoClass}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ChargePlanModal;
